import { useNavigate } from '@tanstack/react-router'
import { motion } from 'motion/react'
import { LuHouse, LuBookOpen, LuArrowLeft, LuCompass } from 'react-icons/lu'

import { Button } from '#/components/ui/button'

type NotFoundPageProps = {
  title?: string
  description?: string
  type?: 'page' | 'course' | 'blog' | 'generic'
}

// ─── Islamic Geometric Pattern Watermark ─────────────────────────────────────
function IslamicTilePattern() {
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.04]"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <pattern
          id="notfound-pattern"
          x="0"
          y="0"
          width="90"
          height="90"
          patternUnits="userSpaceOnUse"
        >
          <g fill="none" stroke="#ffb606" strokeWidth="0.7">
            <polygon points="45,4 49,33 72,20 57,40 85,45 57,50 72,70 49,57 45,86 41,57 18,70 33,50 5,45 33,40 18,20 41,33" />
            <circle cx="45" cy="45" r="16" stroke="#ffb606" strokeWidth="0.4" />
          </g>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#notfound-pattern)" />
    </svg>
  )
}

export default function NotFoundPage({
  title,
  description,
  type = 'page',
}: NotFoundPageProps) {
  const navigate = useNavigate()

  const defaultTitle =
    type === 'course'
      ? 'Course Not Found'
      : type === 'blog'
        ? 'Article Not Found'
        : 'Page Not Found'

  const defaultDescription =
    type === 'course'
      ? 'The course or curriculum you are looking for may have been updated, renamed, or is currently unavailable.'
      : type === 'blog'
        ? 'The article or blog post you requested does not exist or may have been moved.'
        : 'The page you are looking for does not exist, has been removed, or is temporarily unavailable.'

  return (
    <div className="relative min-h-[100vh] flex items-center justify-center overflow-hidden bg-[#041a14] px-6 py-24 text-white">
      <IslamicTilePattern />

      {/* Ambient Radial Mesh Glows */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[500px] w-[500px] rounded-full bg-primary-500/15 blur-[120px]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 right-10 h-[400px] w-[400px] rounded-full bg-secondary-600/20 blur-[100px]"
      />

      <div className="container relative z-10 mx-auto max-w-2xl text-center">
        {/* Animated Badge */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary-500/30 bg-primary-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-primary-300 backdrop-blur-md"
        >
          <LuCompass className="h-3.5 w-3.5 animate-spin-slow" />
          <span>Error 404 — Lost in Learning</span>
        </motion.div>

        {/* Giant Glowing 404 Number */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="relative my-2 select-none"
        >
          <span className="text-8xl sm:text-9xl font-extrabold tracking-tighter bg-gradient-to-b from-primary-300 via-primary-400 to-amber-500 bg-clip-text text-transparent drop-shadow-[0_10px_35px_rgba(255,182,6,0.3)] font-mono">
            404
          </span>
          <div className="absolute inset-0 flex items-center justify-center opacity-10">
            <span className="text-9xl font-black text-white blur-xl">404</span>
          </div>
        </motion.div>

        {/* Heading & Subtext */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="space-y-3"
        >
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
            {title || defaultTitle}
          </h1>
          <p className="text-sm sm:text-base text-neutral-300/80 leading-relaxed max-w-lg mx-auto">
            {description || defaultDescription}
          </p>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center justify-center gap-3.5"
        >
          <Button
            onClick={() => navigate({ to: '/' })}
            className="h-12 cursor-pointer gap-2 rounded-xl bg-primary-500 px-6 font-semibold text-neutral-950 hover:bg-primary-400 shadow-lg shadow-primary-500/25 transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            <LuHouse className="h-4 w-4" />
            Back to Home
          </Button>

          <Button
            variant="outline"
            onClick={() => navigate({ to: '/courses' })}
            className="h-12 cursor-pointer gap-2 rounded-xl border-white/20 bg-white/5 px-6 font-semibold text-white hover:border-primary-400 hover:bg-white/10 hover:text-primary-300 transition-all"
          >
            <LuBookOpen className="h-4 w-4" />
            Explore Courses
          </Button>

          <button
            type="button"
            onClick={() => window.history.back()}
            className="inline-flex h-12 cursor-pointer items-center gap-1.5 rounded-xl px-4 text-sm font-medium text-neutral-300 hover:bg-white/5 hover:text-white transition-all"
          >
            <LuArrowLeft className="h-4 w-4" />
            Go Back
          </button>
        </motion.div>
      </div>
    </div>
  )
}
